import { GraphQL, GraphQLQueryData } from "@rpi/openapi-api";
import React from "react";
import { Outlet, useOutletContext, useParams } from "react-router-dom";
import { useAuthUser } from "../../hooks/useAuthUser";
import { RpiRouteLink } from "../../hooks/useNavigateRoute";
import { createRpiDrawerView, RpiDrawer, RpiDrawerProps } from "../../rpi-core/drawer/RpiDrawer";
import { RpiDrawerField } from "../../rpi-core/drawer/RpiDrawerField";
import { shouldRetry } from "../../utils/query.util";
import { createStyles, Image, Stack } from "@mantine/core";
import { RpaDrawer } from "../../rpi-core/drawer/RpaDrawer";
import { RpiTextField } from "../../components/RpiTextField";
import { getDisplayName } from "./attributeValueMappings";
import { ATTRIBUTES, isSoftcover } from "./attributeHelpers";
import { ProductAttributes, ProductAttributesSchema } from "../../hooks/useStandardProducts";

export const CustomerProductView = createRpiDrawerView<Pick<RpiDrawerProps, "opened" | "onClose">>(
  ({ opened, onClose }) => {
    const { id: stringId } = useParams();
    const id = React.useMemo(() => stringId && Number.parseInt(stringId), []);

    React.useEffect(() => {
      if (!stringId) onClose();
    }, [stringId]);

    const { data, isLoading, error } = GraphQL.useCustomerProductQuery(
      { id: id || -1 },
      {
        enabled: !!stringId && opened,
        refetchOnMount: true,
        retry: shouldRetry(onClose),
      }
    );
    const customerProduct = React.useMemo(() => data?.customerProduct, [data]);

    return (
      <RpiDrawer title="Product details" isLoading={isLoading} error={error} opened={opened} onClose={onClose}>
        {customerProduct && <ProductInfo customerProduct={customerProduct} />}
      </RpiDrawer>
    );
  }
);

interface ProductInfoProps {
  customerProduct: GraphQLQueryData<GraphQL.CustomerProductQuery>;
}

const ProductInfo: React.FC<ProductInfoProps> = ({ customerProduct }) => {
  const user = useAuthUser();

  return (
    <>
      <RpiDrawerField label="Your Name" children={customerProduct.name} />
      <RpiDrawerField label="Description" children={customerProduct.description} />
      <RpiDrawerField label="Your SKU" children={customerProduct.customerSku} />
      {user.is.SUPER_ADMIN && (
        <>
          <RpiDrawerField label="Open API Product">
            <RpiRouteLink
              variant="inherit"
              route={["open-api-product", { id: customerProduct.openapiProductId }]}
              children={customerProduct.openapiProductId}
            />
          </RpiDrawerField>

          <RpiDrawerField label="Customer">
            <RpiRouteLink
              variant="inherit"
              route={["customer", { publicId: customerProduct.customerPublicId }]}
              children={customerProduct.customerPublicId}
            />
          </RpiDrawerField>
        </>
      )}
    </>
  );
};

export const CustomerProductViewV2 = ({ isOpened, onClose }: { isOpened: boolean; onClose: () => void }) => {
  const { id: stringId } = useParams();
  const id = stringId ? Number.parseInt(stringId) : undefined;

  const { data, isLoading, error } = GraphQL.useCustomerProductQuery(
    { id: id || -1 },
    {
      enabled: !!stringId && isOpened,
      refetchOnMount: true,
      retry: shouldRetry(onClose),
    }
  );
  const customerProduct = data?.customerProduct;

  const { data: openapiProductData } = GraphQL.useOpenApiProductQuery(
    { id: customerProduct?.openapiProductId || -1 },
    {
      enabled: !!customerProduct?.openapiProductId && isOpened,
      refetchOnMount: true,
    }
  );

  const openapiProduct = openapiProductData?.openapiProduct;
  const validatedAttributes = (() => {
    if (!openapiProduct?.skuAttributes) return undefined;

    try {
      return ProductAttributesSchema.parse(openapiProduct.skuAttributes);
    } catch (error) {
      console.error("Failed to parse skuAttributes:", error);
      return undefined;
    }
  })();

  return (
    <RpaDrawer
      title={customerProduct?.name || "Product details"}
      isLoading={isLoading}
      error={error}
      opened={isOpened}
      onClose={onClose}
      styles={(theme) => ({
        drawer: {
          width: "360px !important",
          gap: `${theme.spacing.md}px `,
        },
        header: {
          paddingBottom: "0px !important",
        },
      })}
    >
      {customerProduct && openapiProduct && validatedAttributes && (
        <Outlet context={{ customerProduct, openapiProduct, validatedAttributes }} />
      )}
    </RpaDrawer>
  );
};

const useStyles = createStyles((theme) => ({
  content: {
    padding: `0px ${theme.spacing.lg}px ${theme.spacing.lg}px ${theme.spacing.lg}px`,
  },
}));

interface ProductInfoV2Props {
  customerProduct: GraphQLQueryData<GraphQL.CustomerProductQuery>;
  openapiProduct: GraphQLQueryData<GraphQL.OpenApiProductQuery>;
  validatedAttributes: ProductAttributes;
}

export const ProductInfoV2 = () => {
  const { classes } = useStyles();
  const { customerProduct, openapiProduct, validatedAttributes } = useOutletContext<ProductInfoV2Props>();
  const getAttrValue = (attributeKey: string): string | undefined => {
    const value = validatedAttributes?.[attributeKey];
    return value !== undefined ? String(value) : undefined;
  };

  const getDisplayValue = (attributeKey: string, fallback = "-"): string => {
    const rawValue = getAttrValue(attributeKey);
    if (!rawValue) return fallback;
    const displayName = getDisplayName(attributeKey, rawValue);
    return displayName || "LOOKUP ERROR";
  };

  return (
    <Stack className={classes.content} spacing={16}>
      <RpiTextField copyable label="Product SKU" children={customerProduct.customerSku} />
      <RpiTextField copyable label="Manufacturing SKU" children={openapiProduct.openapiSku} />
      <RpiTextField label="Cover Type" children={getDisplayValue(ATTRIBUTES.COVER_TYPE)} />
      <RpiTextField label="Cover Finish" children={getDisplayValue(ATTRIBUTES.COVER_FINISH)} />
      <RpiTextField label="Orientation" children={getDisplayValue(ATTRIBUTES.ORIENTATION)} />
      <RpiTextField label="Size" children={getDisplayValue(ATTRIBUTES.DIMENSIONS)} />
      <RpiTextField label="Bind" children={getDisplayValue(ATTRIBUTES.BIND_TYPE)} />
      <RpiTextField label="Paper Type" children={getDisplayValue(ATTRIBUTES.TEXT_STOCK)} />

      {isSoftcover(validatedAttributes) ? (
        <Image src="/assets/images/book-covers/view_SC_product_details.png" width="100%" fit="contain" radius="sm" />
      ) : (
        <Image src="/assets/images/book-covers/view_HC_product_details.png" width="100%" fit="contain" radius="sm" />
      )}
    </Stack>
  );
};
